import React, { useRef, useState } from 'react';
import { DefaultButton, Dialog, DialogType, DialogFooter, PrimaryButton, IconButton, Stack, Text, Icon, TooltipHost } from '@fluentui/react';
import { t } from 'i18next';
import { useLayer } from 'react-laag';

interface SelectFilesProps {
  files: File[];
  onMultipleFiles: (file: File[]) => void;
  onFileDelete: (index: number) => void;
  disabled: boolean;
}

export default function SelectFiles({ files, onMultipleFiles, onFileDelete, disabled }: SelectFilesProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const handleFileClick = () => {
    if (files.length > 0) {
      setIsDialogOpen(true);
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      onMultipleFiles(selectedFiles);
    }
    e.target.value = '';
  };

  const { renderLayer, layerProps, triggerProps } = useLayer({
    isOpen: isDialogOpen,
    onOutsideClick: () => setIsDialogOpen(false),
    triggerOffset: 6,
    placement: "center",
  });

  return (
    <div>
      <input
        type="file"
        multiple
        hidden
        ref={fileInputRef}
        onChange={handleFileChange}
        disabled={disabled}
      />
      <div {...triggerProps} onClick={handleFileClick}>
        <TooltipHost
          content={
            files.length > 0
              ? `${files.length} archivo(s) seleccionado(s)`
              : t("Seleccione un archivo")
          }
          directionalHint={4}
        >
          <Icon
            iconName="CloudUpload"
            style={{
              fontSize: "30px",
              padding: "2px 20px",
              cursor: disabled ? "default" : "pointer",
              color:
                files.length > 0 ? "#0078D4" : disabled ? "#9E9E9E" : "black",
            }}
            className={files.length > 0 ? "icon-clicked" : ""}
          />
        </TooltipHost>
      </div>

      {isDialogOpen &&
        renderLayer(
          <div
            {...layerProps}
            style={{
              ...layerProps.style,
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              background: "#fff",
              border: "1px solid #ddd",
              borderRadius: 6,
              padding: 0,
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              zIndex: 9999,
            }}
            className={"click-outside-ignore"}
          >
            <h3
              style={{
                marginTop: 0,
                marginBottom: 8,
                fontSize: 18,
                background: "#0078D4",
                color: "white",
                padding: "12px 16px",
                borderTopLeftRadius: 8,
                borderTopRightRadius: 8,
                textAlign: "left",
              }}
            >
              Documentos Adjuntos
            </h3>
            <div style={{ margin:12, display:"flex", flexDirection:"column"}}>
                <p style={{ marginBottom: 10, fontSize: "14px" }}>
                {files.length > 0
                    ? "Tiene los siguientes documentos, puede eliminarlos o agregar más"
                    : "No hay documentos seleccionados"}
                </p>
                <Stack tokens={{ childrenGap: 10 }}>
                {files.map((file, index) => (
                    <Stack
                    horizontal
                    horizontalAlign="space-between"
                    key={index}
                    verticalAlign="center"
                    tokens={{ childrenGap: 10 }}
                    styles={{ root: { alignItems: "center" } }}
                    >
                    <Stack
                        horizontal
                        key={index}
                        verticalAlign="center"
                        tokens={{ childrenGap: 10 }}
                        styles={{ root: { alignItems: "center" } }}
                    >
                        <Icon
                        iconName="CloudUpload"
                        styles={{ root: { fontSize: 18, color: "#0078D4" } }}
                        />
                        <Text
                        style={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "200px",
                        }}
                        >
                        {file.name}
                        </Text>
                    </Stack>
                    <IconButton
                        iconProps={{ iconName: "Delete" }}
                        title="Eliminar"
                        onClick={() => onFileDelete(index)}
                        disabled={disabled}
                    />
                    </Stack>
                ))}
                </Stack>

                <DialogFooter>
                <PrimaryButton
                    text={t("Explorar")}
                    iconProps={{ iconName: "OpenFile" }}
                    onClick={() => fileInputRef.current?.click()}
                    disabled={disabled}
                />
                <DefaultButton
                    text={t("Cerrar")}
                    onClick={() => setIsDialogOpen(false)}
                />
                </DialogFooter>
            </div>
          </div>
        )}
    </div>
  );
}